import { useAdOperatorReportIo } from '@backoffice-frontend/ad-operator-report-io'
import { getAllPassengers } from '@backoffice-frontend/ad-passenger-list'
import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import {
  FccAdAlertType,
  FccPassengerAuthenticationState,
  FccWaypointState,
} from '@backoffice-frontend/graphql'
import {
  FccServiceCancellationStateEnum,
  type FccServiceCancellationTrip,
  FccServiceCancellationTypeEnum,
} from '@backoffice-frontend/graphql/subscription'

import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { CreationContext } from '@backoffice-frontend/report-io-lib'
import * as Sentry from '@sentry/react'
import { useMachine } from '@xstate/react'
import { createContext, type ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { assign, setup } from 'xstate'
import { useAdVehicleStream } from '../AdVehicleStream/useAdVehicleStream'
import {
  type AdVehicleStreamServiceCancellationVehicleFragment,
  useAdVehicleStreamConfirmCancelAllTripsMutation,
  useAdVehicleStreamConfirmCancelSomeTripsMutation,
  useAdVehicleStreamContinueTripsMutation,
  useAdVehicleStreamInitiateServiceCancellationMutation,
  useAdVehicleStreamRestoreMissionMutation,
} from './ServiceCancellation.hook'
import { useAdVehicleStreamServiceCancellationStateSubscription } from './ServiceCancellation.subscription.hook'

type ServiceCancellationValue = {
  cancellationType?: FccServiceCancellationTypeEnum
  initiatedAction?: ServiceCancellationInitiatedAction
  state: ServiceCancellationMachineState
  decision?: ServiceCancellationDecision
  isTripManagementAllowed?: boolean
  initiate: (
    vehicleId: string,
    cancellationType: FccServiceCancellationTypeEnum,
    action: ServiceCancellationInitiatedAction,
  ) => void
  abandon: VoidFunction
  cancelAllTrips: VoidFunction
  continueAllTrips: VoidFunction
  cancelSomeTips: (tripIds: string[]) => void
  trips?: FccServiceCancellationTrip[]
  confirmRestoreMission: VoidFunction
}

export const ServiceCancellationContext = createContext<
  ServiceCancellationValue | undefined
>(undefined)

export type ServiceCancellationDecision =
  | 'cancelAllTrips'
  | 'continueAllTrips'
  | 'restoreMission'
  | 'cancelSomeTrips'

export enum ServiceCancellationInitiatedAction {
  CancelService = 'cancelService',
  ContinueService = 'continueService',
  RestoreMission = 'restoreMission',
  TripManagement = 'tripManagement',
}

type ServiceCancellationProviderProps = {
  vehicle: AdVehicleStreamServiceCancellationVehicleFragment
  children: ReactNode
}

const serviceCancellationStateMachine = setup({
  types: {
    events: {} as
      | {
          type: 'INITIATE'
          cancellationType: FccServiceCancellationTypeEnum
          action: ServiceCancellationInitiatedAction
        }
      | { type: 'INITIATION_SUCCESS' }
      | { type: 'ABANDON' }
      | { type: 'RESTORE_MISSION' }
      | { type: 'CANCEL_ALL_TRIPS' }
      | { type: 'CONTINUE_ALL_TRIPS' }
      | { type: 'CANCEL_SOME_TRIPS'; tripIds: string[] }
      | { type: 'CANCELLATION_SUCCESS' }
      | { type: 'CANCELLATION_ERROR' },
    context: {} as {
      lastCancellationType?: FccServiceCancellationTypeEnum
      initiatedAction?: ServiceCancellationInitiatedAction
      lastConfirmationAction?: ServiceCancellationDecision
      selectedTripIds?: string[]
    },
  },
  actions: {
    resetContext: () => {},
    initiateCancellation: () => {},
    confirmCancelAllTrips: () => {},
    confirmContinueAllTrips: () => {},
    confirmRestoreMission: () => {},
    confirmCancelSomeTrips: () => {},
    onSuccess: () => {},
    onError: () => {},
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiASQDlaAVWgQSYFEBtABgF1EoAA4B7WLgAuuEfkEgAHogC0ARgCsAJhIBOABxqeAFk3btAZgBsu7WoA0IAJ7KLhkoZ4W1al9sO7dlmYAvkH2aFh4hKQEkrjoUvhQdIws7LQA8vQA+gDKAKoAwgUcOTm8AkggouJSMnKKCEoaPCpuxnoaAOymltZ2jogqGrokGmoqZp1Tfn4ahiFhGDgExGT4sfEESawAQqz0ACKZ5XLVsXWVDUpmASSehlMaKrqd-l2d9k6NLm4eXj5+AIWYKhEDhZZRNYbCSQai7fZHegnSpnWqyS7KcZqNxddpqGwWCwtT7OVzuTzeQy+fyBBZgpaRVYxKTxWEFfbFAAyWVYnO5TAASrQAAplfinMTndGgK5eHijLxqXR4wnEgYIMbaHE8Lo8Mz4ondTp08GM6LrFkwiDUAqZFj0PIcHl8rKCkViirCSVo+qDLUWZ48JVPIlmDQBkka-Ha3X67SG7TG0Gmlbm6GwgUlJjpTNZACytFKGSR4pR3uk0oUiDDI06KiJKkbmjUnQeH3VmpjnT1Bp4RpNDNTUMtbI5HG5OXSeadbtFyK9NQrvsaXRGLxU2nUN10Lk0kc7hmasd7-eTg8hmBkADNcAAnVCj+hczlpTK5QrFUrzqrli4y5SWK0AS4poUybl4kYuJ0JAqB4wyGJ4+iHiCiwREOl74De96Ps+r7ZBwAoCjm36okuGKNLBWjmC01idGYiaGIYYaQQ8MFwcqiFqMhA5oRe153g+1rwocxylguUrLkoUEwdoOqvBYXSJgEkZSWSfyUtSQIofSvGrJeqBCFQVrUBAMhgCQsASKyJApnxBlGZAJG-pWDQtmYJBhtoXTAt49FTJGugqK41gbkGGjxjwXk8RCekiPZYDGcJiJOYuf5Vo0UzQVY9Zhs89aMe2Xyqb8FIAjSwLRWaJD6YZCWwgwzBsJwKUSeRZg8PKLxITcUHhgFiajHMQavOFehqJV6FxbVxnsk+47OvyQpzmJP6pS5ygIf6ag3C2dY0vikadBoWhDO19H4vox3jWeumkDVDnWra9D2o6C2uktHoSmty4bncgbBvWerhioh3HTBGhnTYY1XRNdnTRmWY5k6BZFqJnqra1-4IDWJB1g2TZjK2-nqlM2LHe4La6KN+iw7F8UzWOE5TjOH0tT65FNOoOhhh4jbGO122GKDJ0Q3qUOXWMISgvgIgQHAci2cQX2Y+lShU9i2XAk8gUuA8KkQ64QzjDuQzhuYAS09ElBgMr7NY6oZiuJ0hLhSY5hWDYKlBRYdxjM8XTqHMViW8OcQJFAttkfbTzBUG+pge7fSRo2oyvBMdGmJ04ybtpitpiOECR2lVxBVq+KMUePQe-0XxHncYHhcYh6HvWIcYVhglF+t3zhiQLYbrJyreA2kaTFqm7qwGnjNDwuht-x2EQNV6D4JgYAUKwFAUEwt64EIsAFAvndlt9HOU33damLPxiqiD6oIdBhj+47AceEY8+YQJkDVTICQAK5gE3tvXe+9D6f0Xl3SSPttp6m6MMeiFgM4aEjA-Nwz9Dx1jfvMG6MU7pH2-u3ASAo4ASBELeMAeZcCwHEGlUixdECNxIEYJ+gFmytl0CgxBaCqYv0waGD+HcCErzXhQHIcUwA7z3gffBhcT4qyuO1WsG5+7Ah3MYZB98uFPyplBfwqjjDz3ppASBHMMFMNglxDqrYbAqEKogQK2JYLtXkuoA02DUK4J-kYpesA-6YDXtQkx9sx7mKDO4PsVJxh2IQLJLQoZYJ0SDu1bQhj4ZLzALeW8ZCgmq2eCQZUOpeb6i6AgiwkZYl3D1AksMCFklSyCEAA */
  initial: 'idle',
  context: {
    lastCancellationType: undefined,
    initiatedAction: undefined,
    lastConfirmationAction: undefined,
    selectedTripIds: undefined,
  },
  states: {
    idle: {
      entry: 'resetContext',
      on: {
        INITIATE: 'initiating',
      },
    },

    initiating: {
      entry: 'initiateCancellation',
      on: {
        INITIATION_SUCCESS: 'initiated',
        ABANDON: 'idle',
      },
    },

    initiated: {
      on: {
        ABANDON: 'idle',
        CANCEL_ALL_TRIPS: 'confirmed.cancelAllTripsConfirmed',
        CONTINUE_ALL_TRIPS: 'confirmed.continueAllTripsConfirmed',
        RESTORE_MISSION: 'confirmed.confirmRestoreMission',
        CANCEL_SOME_TRIPS: 'confirmed.cancelSomeTripsConfirmed',
      },
    },

    confirmed: {
      initial: 'cancelAllTripsConfirmed',
      on: {
        CANCELLATION_SUCCESS: 'completed.success',
        CANCELLATION_ERROR: 'completed.error',
        ABANDON: 'idle',
      },
      states: {
        cancelAllTripsConfirmed: {
          entry: 'confirmCancelAllTrips',
        },
        continueAllTripsConfirmed: {
          entry: 'confirmContinueAllTrips',
        },
        confirmRestoreMission: {
          entry: 'confirmRestoreMission',
        },
        cancelSomeTripsConfirmed: {
          entry: 'confirmCancelSomeTrips',
        },
      },
    },

    completed: {
      initial: 'success',
      states: {
        success: {
          entry: 'onSuccess',
          type: 'final',
        },
        error: {
          entry: 'onError',
          type: 'final',
        },
      },
      onDone: {
        target: 'idle',
      },
    },
  },
})

export type ServiceCancellationMachineState = ReturnType<
  typeof useMachine<typeof serviceCancellationStateMachine>
>[0]

export const ServiceCancellationProvider = ({
  vehicle,
  children,
}: ServiceCancellationProviderProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification(true)
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { openReportDialog } = useAdOperatorReportIo()
  const { isHandledByCurrentUser } = useAdVehicleStream()
  const [initiateServiceCancellationMutation] = useErrorHandlingMutation(
    useAdVehicleStreamInitiateServiceCancellationMutation,
  )
  const [confirmCancelAllTripsMutation] = useErrorHandlingMutation(
    useAdVehicleStreamConfirmCancelAllTripsMutation,
  )
  const [continueAllTripsMutation] = useErrorHandlingMutation(
    useAdVehicleStreamContinueTripsMutation,
  )
  const [restoreMissionMutation] = useErrorHandlingMutation(
    useAdVehicleStreamRestoreMissionMutation,
  )
  const [confirmCancelSomeTripsMutation] = useErrorHandlingMutation(
    useAdVehicleStreamConfirmCancelSomeTripsMutation,
  )
  const emergencyStopAlertId = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert =>
      alert.type === FccAdAlertType.EmergencyStop && alert.status !== 'CLOSED',
  )?.id

  const [state, send] = useMachine(
    serviceCancellationStateMachine.provide({
      actions: {
        resetContext: assign(() => ({
          lastCancellationType: undefined,
          initiatedAction: undefined,
          lastConfirmationAction: undefined,
          selectedTripIds: undefined,
        })),
        initiateCancellation: assign(({ self, event }) => {
          let cancellationType: FccServiceCancellationTypeEnum | undefined
          let initiatedAction: ServiceCancellationInitiatedAction | undefined
          if (event.type === 'INITIATE') {
            cancellationType = event.cancellationType
            initiatedAction = event.action
            initiateServiceCancellationMutation({
              variables: {
                vehicleId: vehicle.id,
                incidentId: emergencyStopAlertId,
                cancellationType: event.cancellationType,
              },
              onCompleted: () => {
                self.send({ type: 'INITIATION_SUCCESS' })
              },
              onError: () => {
                self.send({ type: 'ABANDON' })
              },
            })
          }
          return {
            lastCancellationType: cancellationType,
            initiatedAction,
            lastConfirmationAction: undefined,
          }
        }),
        confirmRestoreMission: assign(({ self }) => {
          restoreMissionMutation({
            variables: {
              vehicleId: vehicle.id,
            },
            onError: () => {
              self.send({ type: 'CANCELLATION_ERROR' })
            },
          })
          return {
            lastConfirmationAction: 'restoreMission' as const,
          }
        }),
        confirmCancelAllTrips: assign(({ self }) => {
          confirmCancelAllTripsMutation({
            variables: {
              vehicleId: vehicle.id,
            },
            onError: () => {
              self.send({ type: 'CANCELLATION_ERROR' })
            },
          })
          return {
            lastCancellationType: FccServiceCancellationTypeEnum.AllTrips,
            lastConfirmationAction: 'cancelAllTrips' as const,
          }
        }),
        confirmContinueAllTrips: assign(({ self }) => {
          continueAllTripsMutation({
            variables: {
              vehicleId: vehicle.id,
              incidentId: emergencyStopAlertId,
            },
            onError: () => {
              self.send({ type: 'CANCELLATION_ERROR' })
            },
          })
          return {
            lastCancellationType: FccServiceCancellationTypeEnum.AllTrips,
            lastConfirmationAction: 'continueAllTrips' as const,
          }
        }),
        confirmCancelSomeTrips: assign(({ self, event }) => {
          if (event.type === 'CANCEL_SOME_TRIPS') {
            confirmCancelSomeTripsMutation({
              variables: {
                vehicleId: vehicle.id,
                tripIds: event.tripIds,
              },
              onError: () => {
                self.send({ type: 'CANCELLATION_ERROR' })
              },
            })
            return {
              lastCancellationType: FccServiceCancellationTypeEnum.SomeTrips,
              lastConfirmationAction: 'cancelSomeTrips' as const,
              selectedTripIds: event.tripIds,
            }
          }
          return {
            lastCancellationType: FccServiceCancellationTypeEnum.SomeTrips,
            lastConfirmationAction: 'cancelSomeTrips' as const,
          }
        }),
        onSuccess: ({ context }) => {
          if (context.lastConfirmationAction === 'cancelAllTrips') {
            enqueueMoiaSuccessNotification(
              t('adFleetMap_toast_tripsCancelled_caption'),
              { duration: 4000 },
            )
            setTimeout(() => {
              openReportDialog({
                vehicleId: vehicle.id,
                creationContext: CreationContext.FleetMapServiceCancellation,
              })
            }, 1000)
          } else if (context.lastConfirmationAction === 'continueAllTrips') {
            enqueueMoiaSuccessNotification(
              t('adFleetMap_toast_continueAllTripsSuccess_label'),
              { duration: 4000 },
            )
          } else if (context.lastConfirmationAction === 'restoreMission') {
            enqueueMoiaSuccessNotification(
              t('adFleetMap_missionHasBeenRestored_label'),
              { duration: 4000 },
            )
          } else if (context.lastConfirmationAction === 'cancelSomeTrips') {
            const message =
              context.selectedTripIds?.length === 1
                ? t('adFleetMap_toast_tripActionSuccessful_caption')
                : t('adFleetMap_toast_tripActionsSuccessful_caption')

            enqueueMoiaSuccessNotification(message, { duration: 4000 })
            setTimeout(() => {
              openReportDialog({
                vehicleId: vehicle.id,
                creationContext: CreationContext.FleetMapServiceCancellation,
              })
            }, 1000)
          }
        },
        onError: ({ context }) => {
          console.info('History', context.lastConfirmationAction)
          if (context.lastConfirmationAction === 'cancelAllTrips') {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_cancellingServiceError_label'),
            )
          } else if (context.lastConfirmationAction === 'cancelSomeTrips') {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_tripActionsFailed_caption'),
              {
                title: t('adFleetMap_toast_tripActionsFailed_header'),
              },
            )
          } else if (context.lastConfirmationAction === 'continueAllTrips') {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_continueAllTripsFailed_caption'),
            )
          }
        },
      },
    }),
  )

  // Reset service cancellation state when vehicle stream closes
  useEffect(() => {
    if (
      !isHandledByCurrentUser &&
      !state.matches('idle') &&
      !state.matches('initiating')
    ) {
      send({ type: 'ABANDON' })
    }
  }, [isHandledByCurrentUser, state, send])

  const shouldSubscribeToWebsocket =
    !state.matches('idle') && !state.matches('initiating')

  // Handle subscription updates
  const { data: subscriptionData } = useErrorHandlingSubscription(
    useAdVehicleStreamServiceCancellationStateSubscription,
    shouldSubscribeToWebsocket
      ? {
          variables: { vehicleId: vehicle.id },
        }
      : { skip: true },
  )

  const trips = subscriptionData?.fccServiceCancellationState.trips ?? undefined

  const currentCancellationState =
    subscriptionData?.fccServiceCancellationState.state

  const cancellationError =
    subscriptionData?.fccServiceCancellationState.cancellationError

  useEffect(() => {
    if (
      currentCancellationState === FccServiceCancellationStateEnum.Completed
    ) {
      if (cancellationError) {
        send({ type: 'CANCELLATION_ERROR' })
      } else {
        send({ type: 'CANCELLATION_SUCCESS' })
      }
    }
  }, [currentCancellationState, cancellationError, send])

  const passengers = getAllPassengers(vehicle.fccUpcomingWaypoints ?? [])
  const nextStopId = vehicle.fccUpcomingWaypoints?.at(0)?.fccStop?.id
  const isWaypointReached =
    vehicle.fccUpcomingWaypoints?.at(0)?.adState === FccWaypointState.Reached

  const isTripManagementAllowed = passengers.some(
    passenger =>
      (passenger.pickedUpAt !== null &&
        passenger.nextWaypointActionType !== 'PICKUP' &&
        !(
          nextStopId &&
          nextStopId === passenger.fccStop?.id &&
          isWaypointReached
        )) ||
      passenger.authenticationState ===
        FccPassengerAuthenticationState.Upcoming,
  )

  useEffect(() => {
    if (
      state.matches('initiated') &&
      state.context.initiatedAction ===
        ServiceCancellationInitiatedAction.TripManagement &&
      !isTripManagementAllowed
    ) {
      enqueueMoiaErrorNotification(
        t('adFleetMap_toast_noValidTripsForManagement_caption'),
      )
      send({ type: 'ABANDON' })
    }
  }, [state, isTripManagementAllowed, send, enqueueMoiaErrorNotification, t])

  useEffect(() => {
    if (state.matches('initiated') && emergencyStopAlertId) {
      if (
        state.context.initiatedAction ===
        ServiceCancellationInitiatedAction.ContinueService
      ) {
        send({ type: 'CONTINUE_ALL_TRIPS' })
      }
    }
  }, [state, send, emergencyStopAlertId])

  return (
    <ServiceCancellationContext.Provider
      value={{
        cancellationType: state.context.lastCancellationType,
        initiatedAction: state.context.initiatedAction,
        state,
        decision: state.context.lastConfirmationAction,
        isTripManagementAllowed,
        initiate: (
          vehicleId: string,
          cancellationType: FccServiceCancellationTypeEnum,
          action: ServiceCancellationInitiatedAction,
        ) => {
          // MOIA-120312: Verify cancellation is triggered for correct vehicle in case selection changes in the wrong moment.
          if (vehicleId && vehicleId !== vehicle.id) {
            Sentry.captureException(
              new Error(
                `Mismatch of vehicleIds during attempted service cancellation. Selected vehicle from props: ${vehicle.id}, vehicleId from parameter: ${vehicleId}`,
              ),
            )
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_cancellingServiceError_label'),
            )
            return
          }
          send({ type: 'INITIATE', cancellationType, action })
        },
        abandon: () => send({ type: 'ABANDON' }),
        cancelAllTrips: () => send({ type: 'CANCEL_ALL_TRIPS' }),
        continueAllTrips: () => send({ type: 'CONTINUE_ALL_TRIPS' }),
        confirmRestoreMission: () => send({ type: 'RESTORE_MISSION' }),
        cancelSomeTips: (tripIds: string[]) =>
          send({ type: 'CANCEL_SOME_TRIPS', tripIds }),
        trips,
      }}
    >
      {children}
    </ServiceCancellationContext.Provider>
  )
}
